/**
 * /pricing — public pricing page.
 *
 * Plans (real, validated, currently shipped):
 *   1. Free                     — GDD tracker + dashboard, no card
 *   2. Homeowner Seasonal       — $50 / 6 months · 1 month free trial
 *   3. Enthusiast Annual        — $80 / year   · 2 months free trial
 *   4. Inner Circle ⭐ Founding — $0 today / $4.99 locked for life · 10 seats
 *
 * Inner Circle is the visual headline (gradient border, Crown icon, live
 * "X of 10 seats left" counter, InnerCircleCTA component). The other paid
 * tiers route to /profile?tab=plan where the existing subscription
 * checkout flow lives.
 *
 * Pro/Business is intentionally NOT listed — those features (AI satellite
 * scan, property measure) have not been validated for accuracy yet and
 * shipping a price tag for them would damage credibility. Re-add the column
 * once the underlying features are signed off.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { InnerCircleCTA } from '../components/InnerCircleCTA';
import { Crown, Check, Sparkle, ArrowRight, Drop, Heart } from '@phosphor-icons/react';

const Bullet = ({ children, included = true }) => (
  <li className="flex items-start gap-2 text-sm">
    <Check
      size={16}
      weight={included ? 'bold' : 'regular'}
      className={
        included
          ? 'mt-0.5 shrink-0 text-emerald-600'
          : 'mt-0.5 shrink-0 text-zinc-300 dark:text-zinc-600'
      }
    />
    <span className={included ? 'text-zinc-700 dark:text-zinc-200' : 'text-zinc-400 line-through'}>
      {children}
    </span>
  </li>
);

export default function PricingPage() {
  // Smart tier CTA — anonymous users get bounced through auth with a
  // surviving intent param (HomeOrHub in App.js fires the matching checkout
  // post-auth). Logged-in users go straight to their profile's plan tab where
  // tier-management UI lives. Fixes the same conversion-killer pattern as
  // the Inner Circle CTA: don't drop visitors on a dashboard with no
  // payment path.
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleTierStart = (intent) => {
    if (!user) {
      navigate(`/?auth=1&intent=${intent}`);
    } else {
      navigate('/profile?tab=plan');
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-950 dark:to-zinc-900"
      data-testid="pricing-page"
    >
      <SEO
        title="Pricing — TurfPulse"
        description="TurfPulse pricing — Free GDD tracker forever, Homeowner Seasonal at $50/6mo, Enthusiast Annual at $80/yr, and the $4.99/mo Inner Circle founding-member tier (10 seats, locked for life)."
        canonical="https://getturfpulse.com/pricing"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Pricing', path: '/pricing' },
        ]}
      />
      <FaqJsonLd
        items={[
          {
            q: "What's the difference between Homeowner Seasonal and Enthusiast Annual?",
            a: 'Homeowner Seasonal is $50 for 6 months and unlocks the PGR Department, Weed Control, equipment + spreader settings, email alerts, and up to 5 zones per property. Enthusiast Annual is $80/year and adds Pest Management, Disease Management, Soil Analysis, the 5-Day Spray Forecast, unlimited zones, and priority support. Both come with a free trial.',
          },
          {
            q: "What's the catch with the Inner Circle $4.99/mo rate?",
            a: "There isn't one. We're capping at 10 founding members so we can actually answer your messages and ship features you ask for. After Oct 31, 2026 your card gets charged $4.99/mo and that rate is locked for as long as your subscription stays active. Cancel anytime.",
          },
          {
            q: 'Why is the founding tier almost free? What\'s the trade-off?',
            a: 'You become product partners. We DM you when something ships. Your bug reports skip the queue. Your feature requests carry weight. Your monthly cost stays at $4.99 forever, while everyone who signs up after the founding cohort pays the regular tier price (TBD, but higher).',
          },
          {
            q: 'Do I need to buy hardware to use TurfPulse?',
            a: 'No. The free tier (GDD tracking, dashboard, dewpoint, dollar-spot pressure) works on weather data alone. Hardware (Ecowitt soil probes + Rachio controller) unlocks the closed-loop probe-driven irrigation flow — but you can use the free brain without ever burying a sensor.',
          },
          {
            q: 'Can I cancel anytime?',
            a: 'Yes. Cancel from your profile or by emailing hello@turfpulse.app. No retention scripts, no dark patterns. If you cancel before Nov 1, 2026 (founding-tier free period), you owe nothing.',
          },
        ]}
      />

      {/* Hero */}
      <section className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="mx-auto max-w-5xl px-6 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            <Sparkle size={14} weight="fill" /> Honest pricing · No dark patterns
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Free forever, or join the Inner Circle.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-700 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
            The GDD tracker is permanently free. Paid tiers unlock the
            departments you'll actually use — PGR, Weed Control, Pest, Disease,
            and the closed-loop probe-driven irrigation engine. We're capping
            the founding tier at <strong>10 seats</strong> so we can answer
            your messages personally.
          </p>
        </div>
      </section>

      {/* Plans grid — 4 tiers, Inner Circle highlighted */}
      <section className="mx-auto max-w-7xl px-6 py-12 sm:py-16">
        <div className="grid gap-6 lg:grid-cols-4">
          {/* ── Free ────────────────────────────────────────────────────── */}
          <Card
            className="relative border-zinc-200 dark:border-zinc-800"
            data-testid="pricing-free"
          >
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-zinc-100 dark:bg-zinc-800 px-3 py-1 text-[11px] font-bold text-zinc-600 dark:text-zinc-300 uppercase tracking-widest">
                  Free · Forever
                </div>
                <h2 className="mt-3 text-2xl font-black text-zinc-900 dark:text-zinc-50">
                  Free
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  The GDD brain on weather data alone — no card needed.
                </p>
              </div>
              <div>
                <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50">$0</span>
                <span className="ml-1 text-sm text-zinc-500">/forever</span>
              </div>
              <ul className="space-y-2.5">
                <Bullet>Daily GDD tracking (base 50 + base 55)</Bullet>
                <Bullet>Weather Dashboard + 7-Day Forecast</Bullet>
                <Bullet>Pre-emergent + PGR rebound timing windows</Bullet>
                <Bullet>Open methodology + Excel worksheet</Bullet>
                <Bullet>Round Rock smart-irrigation rebate helper</Bullet>
                <Bullet included={false}>PGR / Weed / Pest / Disease departments</Bullet>
                <Bullet included={false}>Equipment + spreader settings log</Bullet>
                <Bullet included={false}>Email alerts</Bullet>
              </ul>
              <Link to="/?auth=1">
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="pricing-free-cta"
                >
                  Create free account <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* ── Homeowner Seasonal ──────────────────────────────────────── */}          <Card
            className="relative border-zinc-200 dark:border-zinc-800"
            data-testid="pricing-homeowner"
          >
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 dark:bg-blue-900/40 px-3 py-1 text-[11px] font-bold text-blue-800 dark:text-blue-200 uppercase tracking-widest">
                  Homeowner · Seasonal
                </div>
                <h2 className="mt-3 text-2xl font-black text-zinc-900 dark:text-zinc-50">
                  Homeowner
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  For one or two properties. Get the departments you'll use weekly.
                </p>
              </div>
              <div>
                <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50">$50</span>
                <span className="ml-1 text-sm text-zinc-500">/6 months</span>
                <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  1 month free trial
                </p>
              </div>
              <ul className="space-y-2.5">
                <Bullet><strong>Everything in Free, plus:</strong></Bullet>
                <Bullet>PGR Department + Weed Control Department</Bullet>
                <Bullet>Equipment Log + Spreader Settings</Bullet>
                <Bullet>Seasonal Calendar + Email Alerts</Bullet>
                <Bullet>Up to 3 properties · 5 zones each</Bullet>
                <Bullet>Application Logging (unlimited)</Bullet>
                <Bullet>Partners Directory access</Bullet>
                <Bullet included={false}>Pest / Disease departments</Bullet>
                <Bullet included={false}>Soil Analysis + 5-Day Spray Forecast</Bullet>
              </ul>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleTierStart('homeowner-seasonal')}
                data-testid="pricing-homeowner-cta"
              >
                Start free trial <ArrowRight size={16} className="ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* ── Inner Circle ─ HEADLINE ─────────────────────────────────── */}
          <Card
            className="relative border-2 border-emerald-500 dark:border-emerald-500 shadow-xl lg:scale-[1.04] bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-emerald-950/40 dark:via-zinc-950 dark:to-amber-950/30 z-10"
            data-testid="pricing-inner-circle"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-white shadow whitespace-nowrap">
              <Crown size={12} weight="fill" /> Founding · 10 seats only
            </div>
            <CardContent className="p-6 pt-9 space-y-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-600/15 dark:bg-emerald-700/30 px-3 py-1 text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-widest">
                  Inner Circle
                </div>
                <h2 className="mt-3 text-2xl font-black text-zinc-900 dark:text-zinc-50">
                  Inner Circle
                </h2>
                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                  Card on file today, $0 charged. Free until Oct 31, 2026 — then locked for life.
                </p>
              </div>
              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50">$4.99</span>
                  <span className="text-sm text-zinc-500">/mo</span>
                </div>
                <span className="mt-1 inline-block text-[10px] font-bold uppercase tracking-widest rounded bg-amber-200 dark:bg-amber-700/40 px-1.5 py-0.5 text-amber-900 dark:text-amber-200">
                  Locked for life
                </span>
                <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                  $0 today · first charge Nov 1, 2026 · cancel anytime
                </p>
              </div>
              <ul className="space-y-2.5">
                <Bullet><strong>Everything in Enthusiast, plus:</strong></Bullet>
                <Bullet>Probe-driven runtime engine (24/7 Ecowitt → Rachio loop)</Bullet>
                <Bullet>TAMU 12% drought-floor override</Bullet>
                <Bullet>Smart-Split + Even-Split recurring schedules</Bullet>
                <Bullet>Sensor-gated skip stack (rain · freeze · wind · MAD)</Bullet>
                <Bullet>Outbound HA webhooks + 10 personal API tokens</Bullet>
                <Bullet>Founding-member crest on dashboard</Bullet>
                <Bullet>Direct line to the founder for feature requests</Bullet>
              </ul>
              <div className="pt-1">
                <InnerCircleCTA testId="pricing-inner-circle-cta" size="default" />
              </div>
            </CardContent>
          </Card>

          {/* ── Enthusiast Annual ───────────────────────────────────────── */}
          <Card
            className="relative border-zinc-200 dark:border-zinc-800"
            data-testid="pricing-enthusiast"
          >
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 dark:bg-violet-900/40 px-3 py-1 text-[11px] font-bold text-violet-800 dark:text-violet-200 uppercase tracking-widest">
                  Enthusiast · Annual
                </div>
                <h2 className="mt-3 text-2xl font-black text-zinc-900 dark:text-zinc-50">
                  Enthusiast
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  For the power user. Every department, every property, no caps.
                </p>
              </div>
              <div>
                <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50">$80</span>
                <span className="ml-1 text-sm text-zinc-500">/year</span>
                <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  2 months free trial
                </p>
              </div>
              <ul className="space-y-2.5">
                <Bullet><strong>Everything in Homeowner, plus:</strong></Bullet>
                <Bullet>Pest Management Department</Bullet>
                <Bullet>Disease Management Department</Bullet>
                <Bullet>Soil Analysis</Bullet>
                <Bullet>5-Day Spray Forecast</Bullet>
                <Bullet>Unlimited zones + properties</Bullet>
                <Bullet>Priority Support</Bullet>
                <Bullet>Personal Weather Station integration</Bullet>
              </ul>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleTierStart('enthusiast-annual')}
                data-testid="pricing-enthusiast-cta"
              >
                Start free trial <ArrowRight size={16} className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why founding tier exists */}
      <section
        className="border-y border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950"
        data-testid="pricing-founding-rationale"
      >
        <div className="mx-auto max-w-3xl px-6 py-14">
          <div className="flex items-center gap-2">
            <Heart size={26} weight="fill" className="text-rose-500" />
            <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-zinc-50">
              Why $4.99/mo for the founding tier?
            </h2>
          </div>
          <p className="mt-4 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">
            Honest answer: <strong>we want product partners, not customers.</strong> A
            handful of people who run probes in their own yards, hit bugs we
            haven't seen, and ask for features that make TurfPulse better for
            everyone who signs up later. That's worth more to us than the
            margin we'd make charging $80/year to a stranger.
          </p>
          <p className="mt-3 text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">
            The 10-seat cap is real. It exists so we can <strong>answer your
            DMs personally</strong> — not because of some artificial scarcity
            playbook. After it fills, the founding tier closes; standard
            Enthusiast pricing applies and your founding rate stays locked
            for as long as your subscription is active.
          </p>
          <div className="mt-6 rounded-lg border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 p-5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300">
              The founding-member promise
            </div>
            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Card captured today, <strong>$0 charged today</strong>. First charge of
              $4.99 hits Nov 1, 2026 — then locked at that rate forever.
              You'll get the founding-member crest on your dashboard, a direct
              line to the founder, and the feeling of being someone who
              actually shaped this thing.
            </p>
          </div>
        </div>
      </section>

      {/* Receipts CTA */}
      <section className="mx-auto max-w-3xl px-6 py-14 text-center" data-testid="pricing-receipts-cta">
        <div className="inline-flex items-center gap-2">
          <Drop size={22} weight="duotone" className="text-emerald-700 dark:text-emerald-400" />
          <h3 className="text-xl font-black text-zinc-900 dark:text-zinc-50">
            We don't ask you to trust us — we cite our sources.
          </h3>
        </div>
        <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300">
          Every threshold we use traces back to a peer-reviewed paper or a
          public-domain government feed. Read the methodology, copy the math,
          DIY it in Excel. Pay us only if you'd rather we run it for you.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link
            to="/methodology/probe-driven-irrigation"
            className="inline-flex items-center gap-1 rounded-md bg-emerald-700 hover:bg-emerald-800 px-4 py-2 text-sm font-semibold text-white"
            data-testid="pricing-methodology-link"
          >
            See the methodology <ArrowRight size={14} className="ml-1" />
          </Link>
          <Link
            to="/credits"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-900"
            data-testid="pricing-credits-link"
          >
            Read our citations
          </Link>
        </div>
      </section>
    </div>
  );
}
